import { useState } from 'react'
import { Link, useParams, useNavigate, Navigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronRight, FileText, Plus, GripVertical, KeyRound } from 'lucide-react'
import { DndContext, closestCenter, PointerSensor, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { api } from '../../lib/api.js'
import { useOnline } from '../../lib/useOnline.js'
import { fmtDate } from '../../lib/date.js'
import { toast } from '../../components/feedback/Toast.jsx'
import SwipeRow from '../../components/SwipeRow.jsx'
import { cn } from '../../lib/cn.js'

export default function SpacePage() {
  const { space } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: () => api('/auth/me'), staleTime: Infinity })
  // null = closed; { section } = the new-page box is open — at the top for a
  // plain new page (section null), or under that heading (the + on it).
  const [adding, setAdding] = useState(null)
  const [busy, setBusy] = useState(false)
  const online = useOnline()

  const { data, isLoading, error } = useQuery({
    queryKey: ['space', space],
    queryFn: () => api(`/spaces/${space}/pages`),
  })

  if (error?.status === 404) return <Navigate to="/search" replace />
  if (isLoading || !data) return <p className="text-sm text-muted">Loading…</p>

  const { space: meta, pages } = data
  // Offline is read-only — no "New page" against the synced copy.
  const canEdit = ['admin', 'editor'].includes(me.data?.user?.role) && online

  async function deletePage(p) {
    if (!window.confirm(`Delete “${p.title}”?`)) return
    try {
      await api(`/pages/${p.id}`, { method: 'DELETE' })
      qc.invalidateQueries({ queryKey: ['space', space] })
      qc.invalidateQueries({ queryKey: ['spaces'] })
    } catch (err) {
      toast(err.message || 'Couldn’t delete', 'error')
    }
  }

  // Drag-to-reorder, Notion-style (Charles, 2026-08-03): optimistic — the
  // list moves immediately, the server learns the new order in the
  // background, and a failed save snaps back to the truth. A page dragged
  // under another heading takes that heading with it (2026-09-29); only the
  // pages whose heading changed are sent as `sections`.
  function onReorder(next) {
    const before = new Map(pages.map((p) => [p.id, p.section ?? null]))
    const moved = next.filter((p) => (p.section ?? null) !== before.get(p.id))
    qc.setQueryData(['space', space], (d) => ({ ...d, pages: next }))
    api(`/spaces/${space}/pages/order`, {
      method: 'PUT',
      body: {
        ids: next.map((p) => p.id),
        ...(moved.length ? { sections: Object.fromEntries(moved.map((p) => [p.id, p.section ?? null])) } : {}),
      },
    }).catch(() => {
      toast('Couldn’t save the order', 'error')
      qc.invalidateQueries({ queryKey: ['space', space] })
    })
  }

  async function createPage(rawTitle, section) {
    const title = (rawTitle || '').trim()
    if (!title) return setAdding(null)
    setBusy(true)
    try {
      const res = await api(`/spaces/${space}/pages`, { body: { title, ...(section ? { section } : {}) } })
      qc.invalidateQueries({ queryKey: ['space', space] })
      qc.invalidateQueries({ queryKey: ['spaces'] })
      // Land straight in the editor — a brand-new page is there to be written.
      navigate(`/${space}/${res.page.slug}`, { state: { edit: true } })
    } catch (err) {
      toast(err.message || 'Couldn’t create page', 'error')
      setBusy(false)
    }
  }

  const newPageBox = (section) => (
    <NewPageInput
      busy={busy}
      placeholder={section ? `New page in ${section}…` : 'Page title…'}
      onCreate={(title) => createPage(title, section)}
      onCancel={() => setAdding(null)}
    />
  )

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3 border-b border-hairline pb-3">
        <h1 className="flex items-center gap-2 text-2xl font-semibold text-ink">
          {meta.icon && <span className="text-xl grayscale">{meta.icon}</span>}
          {meta.name}
        </h1>
        {canEdit && adding === null && (
          <button
            onClick={() => setAdding({ section: null })}
            className="tap flex shrink-0 items-center gap-1.5 rounded-xl bg-navy px-3.5 py-2 text-sm font-medium text-white"
          >
            <Plus size={15} /> New page
          </button>
        )}
      </div>

      {adding && !adding.section && <div className="mb-4">{newPageBox(null)}</div>}

      {/* The Managerial section keeps its own login store (Charles,
          2026-08-03) — pinned above the pages, admin eyes only (non-admins
          never see this space at all). */}
      {meta.slug === 'management' && (
        <Link to="/management/logins" className="card mb-3 flex items-center gap-3 px-4 py-3 active:bg-grouped">
          <KeyRound size={16} className="shrink-0 text-faint" />
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">Logins</span>
          <ChevronRight size={16} className="shrink-0 text-faint" />
        </Link>
      )}

      <PageList
        pages={pages}
        space={space}
        canEdit={canEdit}
        onDelete={deletePage}
        onReorder={onReorder}
        addingTo={adding?.section || null}
        onAdd={(section) => setAdding({ section })}
        newPageBox={newPageBox}
      />
    </div>
  )
}

/** The title box for a new page: Enter creates, Escape (or Cancel) closes. */
function NewPageInput({ busy, placeholder, onCreate, onCancel }) {
  const [title, setTitle] = useState('')
  return (
    <div className="flex gap-2">
      <input
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onCreate(title)
          if (e.key === 'Escape') onCancel()
        }}
        placeholder={placeholder}
        className="tap min-w-0 flex-1 rounded-xl border border-hairline bg-white px-4 py-2.5 text-[16px] focus:outline-none focus:ring-2 focus:ring-link"
      />
      <button onClick={() => onCreate(title)} disabled={busy || !title.trim()} className="tap rounded-xl bg-navy px-4 text-sm font-medium text-white disabled:opacity-40">
        Create
      </button>
      <button onClick={onCancel} className="tap rounded-xl px-3 text-sm text-muted">
        Cancel
      </button>
    </div>
  )
}

// Rows that read as the front door of their section wear a bold title
// (Charles, 2026-09-26: General Studio Info and End of the day under Start
// here, Rooms at a glance in Studio Info). Matched by slug, so a row keeps
// its weight wherever it is dragged; the pages themselves are ordinary.
const BOLD_ROWS = new Set(['guides/how-to-use-this-app', 'guides/general-studio-info', 'guides/room-shut-down', 'studios/rooms-at-a-glance'])

// The headings in a space's list (Guides: Start here, Video, Phones…) are
// the pages' own `section` — stored on each page since 2026-09-29, when a
// new page couldn't be dragged into Video (the headings used to be a slug
// list here, so an unlisted page always read "Other guides"). A heading is
// drawn wherever the section changes reading down the list; a space where
// no page has a section (every space but Guides today) shows none.
const otherLabel = (space) => (space === 'guides' ? 'Other guides' : 'Other pages')

/** The list as rows AND headings, in reading order — the headings take part
 *  in the drag (as places to drop, never picked up themselves). */
function listItems(pages, space) {
  if (!pages.some((p) => p.section)) return pages.map((p) => ({ kind: 'page', id: p.id, page: p }))
  const items = []
  let prev
  pages.forEach((p, i) => {
    const section = p.section || null
    if (i === 0 || section !== prev) {
      items.push({ kind: 'heading', id: `heading-${items.length}`, section, label: section || otherLabel(space) })
    }
    items.push({ kind: 'page', id: p.id, page: p })
    prev = section
  })
  return items
}

/**
 * The space's page list. For editors each row grows a grip handle — drag
 * it (mouse or finger; touch-none keeps the page from scrolling under the
 * drag) to reorder, Notion-style — and the row body swipes left to delete
 * on phones. Dropping a page under a heading puts it in that section, and
 * each named heading has a + to start a page right there. Viewers get the
 * plain list.
 */
function PageList({ pages, space, canEdit, onDelete, onReorder, addingTo, onAdd, newPageBox }) {
  // Pointer covers modern browsers; Mouse/Touch are fallbacks for anything
  // that synthesizes classic events. Touch waits 150ms so a scroll that
  // begins on the handle still scrolls.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } })
  )
  const items = listItems(pages, space)

  const row = (p) => {
    const bold = BOLD_ROWS.has(`${space}/${p.slug}`)
    return (
      <Link to={`/${space}/${p.slug}`} className="flex items-center gap-3 bg-white px-4 py-3 active:bg-grouped">
        <FileText size={16} className="shrink-0 text-faint" />
        <div className="min-w-0 flex-1">
          <div className={`truncate text-sm text-ink ${bold ? 'font-bold' : 'font-medium'}`}>{p.title}</div>
          <div className="mt-0.5 text-xs text-faint">
            {p.childCount > 0 && <span>{p.childCount} subpages · </span>}
            updated {fmtDate(p.updatedAt)}
          </div>
        </div>
        <ChevronRight size={16} className="shrink-0 text-faint" />
      </Link>
    )
  }

  // The new-page box opens under the FIRST heading of its section.
  const firstOf = new Map()
  items.forEach((it) => it.kind === 'heading' && !firstOf.has(it.section) && firstOf.set(it.section, it.id))

  const heading = (it) => (
    <div className="bg-grouped/60">
      <div className="flex items-center justify-between gap-2 pl-4 pr-2">
        {/* "Start here" is the one heading drawn as a solid navy tag
            (Charles, 2026-09-26: it replaced the tag that sat on the
            General Studio Info row). */}
        {it.section === 'Start here' ? (
          <div className="pb-2 pt-3">
            <span className="inline-block rounded-md bg-navy px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
              {it.label}
            </span>
          </div>
        ) : (
          <div className="section-header pb-1.5 pt-3 text-[11px] text-muted">{it.label}</div>
        )}
        {canEdit && it.section && (
          <button
            type="button"
            onClick={() => onAdd(it.section)}
            aria-label={`Add a page to ${it.label}`}
            title={`Add a page to ${it.label}`}
            className="tap -my-1 flex items-center justify-center rounded-lg text-faint active:bg-white active:text-navy"
          >
            <Plus size={16} />
          </button>
        )}
      </div>
      {canEdit && it.section && addingTo === it.section && firstOf.get(it.section) === it.id && (
        <div className="px-3 pb-3 pt-1">{newPageBox(it.section)}</div>
      )}
    </div>
  )

  if (!canEdit) {
    return (
      <div className="card divide-y divide-hairline overflow-hidden">
        {items.map((it) => (
          <div key={it.id}>{it.kind === 'heading' ? heading(it) : row(it.page)}</div>
        ))}
        {pages.length === 0 && <p className="px-4 py-8 text-center text-sm text-muted">Nothing here yet.</p>}
      </div>
    )
  }

  // Each page belongs to the nearest heading above it once dropped — which
  // is what the screen shows. One that lands above every heading joins the
  // first one.
  function onDragEnd({ active, over }) {
    if (!over || active.id === over.id) return
    const ids = items.map((it) => it.id)
    const moved = arrayMove(items, ids.indexOf(active.id), ids.indexOf(over.id))
    const first = moved.find((it) => it.kind === 'heading')
    let current
    const next = []
    for (const it of moved) {
      if (it.kind === 'heading') current = it.section
      else next.push({ ...it.page, section: current !== undefined ? current : first ? first.section : it.page.section ?? null })
    }
    onReorder(next)
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={items.map((it) => it.id)} strategy={verticalListSortingStrategy}>
        <div className="card divide-y divide-hairline overflow-hidden">
          {items.map((it) =>
            it.kind === 'heading' ? (
              <SortableHeading key={it.id} id={it.id}>
                {heading(it)}
              </SortableHeading>
            ) : (
              <SortableRow key={it.id} id={it.id}>
                <SwipeRow onDelete={() => onDelete(it.page)}>{row(it.page)}</SwipeRow>
              </SortableRow>
            )
          )}
          {pages.length === 0 && <p className="px-4 py-8 text-center text-sm text-muted">Nothing here yet.</p>}
        </div>
      </SortableContext>
    </DndContext>
  )
}

/** A heading in the drag: it moves aside for a page, and a page can be
 *  dropped on either side of it, but it can't be picked up itself. */
function SortableHeading({ id, children }) {
  const { setNodeRef, transform, transition } = useSortable({ id, disabled: { draggable: true, droppable: false } })
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }}>
      {children}
    </div>
  )
}

function SortableRow({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={isDragging ? 'relative z-10 opacity-90 shadow-md' : ''}
    >
      <div className="flex items-stretch bg-white">
        <button
          type="button"
          {...attributes}
          {...listeners}
          // the handle is dnd's alone — SwipeRow must never see its touches
          onTouchStart={(e) => e.stopPropagation()}
          aria-label="Reorder"
          className="flex w-9 shrink-0 cursor-grab touch-none items-center justify-center text-faint active:cursor-grabbing"
        >
          <GripVertical size={15} />
        </button>
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </div>
  )
}
